import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { useResourceUtilizationChart } from "./hooks/useResourceUtilizationChart";
import { computeDisplayRange } from "./utils/periodRange";
import { getBerlinToday, formatDateOnly, formatBerlinDisplay } from "../../../utils/berlinDate";
import { trimFutureBuckets } from "../_shared/comparisonPeriod";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import { useOcelMetadata } from "../../../stores/OcelContext";
import { formatAttributeValue } from "../../visualization/GanttChart/ganttTransform";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileChartContent,
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import ResourceUtilizationChartSettings from "./ResourceUtilizationChartSettings";
import * as Tooltip from "@radix-ui/react-tooltip";
import DateRangePresets from "../../../features/filter/TabFilterModal/DateRangePresets";
import { PinnedInfoCard } from "../../../components/common/tooltip/TooltipSurfaces";
import ObjectAttributeTable from "../../../components/common/ObjectAttributeTable";
import { useObjectTableFetch } from "../../../hooks/useObjectTableFetch";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
);

const MOVING_AVERAGE_WINDOW = { day: 7, week: 4, month: 7, year: 3 };

/** Trailing arithmetic mean; null until the window fills (no partial/growing-window average). */
function computeMovingAverage(values, windowSize) {
  return values.map((_, i) => {
    if (i < windowSize - 1) return null;
    let sum = 0;
    for (let j = i - windowSize + 1; j <= i; j++) sum += Number(values[j] || 0);
    return sum / windowSize;
  });
}

let _hatchPattern = null;
function getHatchPattern() {
  if (_hatchPattern) return _hatchPattern;
  if (typeof document === "undefined") return null;
  const size = 8;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "rgba(229, 231, 235, 0.5)";
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = "rgba(107, 114, 128, 0.8)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, size);
  ctx.lineTo(size, 0);
  ctx.stroke();
  _hatchPattern = ctx.createPattern(canvas, "repeat");
  return _hatchPattern;
}

const MOVING_AVERAGE_INK = "#374151";

/** Paints a line+dot legend swatch for the Moving Average entry, replacing
 *  Chart.js's default box renderer (which reads a misleading solid block at
 *  this size). Scoped to this chart instance only via the `plugins` prop —
 *  never registered globally. Finds the flagged legend item's reserved,
 *  left-blank box via legendHitBoxes and draws into it after Chart.js's own
 *  legend draw completes. */
const movingAverageLegendIconPlugin = {
  id: "movingAverageLegendIcon",
  afterDraw(chart) {
    const legend = chart.legend;
    const idx = legend?.legendItems?.findIndex((item) => item.isMovingAverageIcon);
    const box = idx != null && idx >= 0 ? legend.legendHitBoxes?.[idx] : null;
    if (!box) return;
    const boxWidth = legend.options?.labels?.boxWidth ?? 40;
    const ctx = chart.ctx;
    const cy = box.top + box.height / 2;
    const x1 = box.left + 3;
    const x2 = box.left + boxWidth - 3;
    ctx.save();
    ctx.strokeStyle = MOVING_AVERAGE_INK;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x1, cy);
    ctx.lineTo(x2, cy);
    ctx.stroke();
    ctx.beginPath();
    ctx.fillStyle = MOVING_AVERAGE_INK;
    ctx.arc((x1 + x2) / 2, cy, 2.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  },
};

const MIN_VISIBLE_UTILIZATION_PCT = 1;
const MIN_VISIBLE_ZERO_UTILIZATION_PCT = 0.2;

function formatXAxisDateLabel(value, granularity) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  if (granularity === "year") {
    return parsed.toLocaleDateString("de-DE", { month: "short" });
  }
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
  });
}

function formatGermanDateLabel(value) {
  const parsed = new Date(value + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Default anchor date per granularity. */
function defaultAnchor(gran) {
  const localDateStr = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const today = new Date();
  if (gran === "day") {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    return localDateStr(y);
  }
  return localDateStr(today);
}

/** Isolated date input — uncontrolled to prevent Chrome's date-section cursor from
 *  resetting on every React re-render.
 *
 *  Chrome fires native 'change' on each section auto-advance (month→day→year) during
 *  keyboard typing, NOT only when the picker closes. We distinguish the two paths via
 *  a pointer flag: if the last interaction was a pointer event (click = calendar picker),
 *  commit on 'change'; if it was a key event (keyboard typing), defer to Enter. */
const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  const committed = useRef(value);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const isPointerRef = useRef(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Sync from parent when value changes externally (e.g. granularity button click)
  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value;
    committed.current = value;
  }, [value]);

  const commit = useCallback(() => {
    const val = inputRef.current?.value;
    if (!val || val === committed.current) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val) || isNaN(new Date(val + "T00:00:00").getTime())) return;
    committed.current = val;
    onChangeRef.current(val);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onPointerDown = () => { isPointerRef.current = true; };
    const onKeyDown = () => { isPointerRef.current = false; };
    const onNativeChange = () => {
      if (isPointerRef.current) {
        isPointerRef.current = false;
        commit();
      }
    };
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('change', onNativeChange);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('change', onNativeChange);
    };
  }, [commit]);

  return (
    <input
      ref={inputRef}
      type="date"
      defaultValue={value}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      onBlur={commit}
      disabled={disabled}
      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
    />
  );
});

const SummaryStats = ({ chartData }) => {
  const { t } = useTranslation();
  if (!chartData) return null;
  const fmt = (n, decimals = 2) => Number(n || 0).toFixed(decimals);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
        <div className="text-xs text-gray-600 font-medium">
          {t('tiles:utilization.overall')}
        </div>
        <div className="text-lg font-bold text-primary-600">
          {fmt(chartData.overall_utilization_percentage)}%
        </div>
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
        <div className="text-xs text-gray-600 font-medium">{t('tiles:utilization.totalDowntime')}</div>
        <div className="text-lg font-bold text-primary-600">
          {chartData.total_downtime_seconds >= 3600
            ? `${fmt(chartData.total_downtime_seconds / 3600)} ${t('common:units.hrs')}`
            : `${fmt(chartData.total_downtime_seconds / 60, 1)} ${t('common:units.min')}`}
        </div>
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
        <div className="text-xs text-gray-600 font-medium">{t('tiles:utilization.daysAnalyzed')}</div>
        <div className="text-lg font-bold text-primary-600">
          {chartData.days_analyzed}
        </div>
      </div>
    </div>
  );
};

function ResourceUtilizationChart({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const { t } = useTranslation();
  const {
    loading,
    error,
    chartData,
    datasetLastDate,
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    tooltipAttributes,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    enabledGranularities,
    comparisonEnabled,
    comparisonMode,
    comparisonPeriodsCount,
    limitComparisonToCurrentProgress,
    showMovingAverage,
    // ViewState
    viewResources,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    rollingOffset,
    resourceLastActiveDate,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setConfiguredResources,
    setTooltipAttributes,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setEnabledGranularities,
    setComparisonEnabled,
    setComparisonMode,
    setComparisonPeriodsCount,
    setLimitComparisonToCurrentProgress,
    setShowMovingAverage,
    // ViewState setters
    setViewResources,
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    setRollingOffset,
    // Break helpers
    addBreak,
    removeBreak,
    updateBreak,
    // Actions
    fetchMetadata,
    fetchChartData,
    fetchObjectBreakdown,
  } = useResourceUtilizationChart(activeFile, initialSettings);

  const metadata = useOcelMetadata(activeFile);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, selectedObjectType, cycleStartActivity, cycleEndActivity, configuredResources,
      tooltipAttributes,
      shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNonWorkingDays, includeNoDataResources, multiResourceMode, enabledGranularities,
      comparisonEnabled, comparisonMode, comparisonPeriodsCount, limitComparisonToCurrentProgress, showMovingAverage,
      viewResources, granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd, rollingOffset,
    }));
  }, [title, selectedObjectType, cycleStartActivity, cycleEndActivity, // eslint-disable-line react-hooks/exhaustive-deps
      configuredResources, tooltipAttributes, shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNonWorkingDays, includeNoDataResources, multiResourceMode, enabledGranularities,
      comparisonEnabled, comparisonMode, comparisonPeriodsCount, limitComparisonToCurrentProgress, showMovingAverage,
      viewResources, granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd, rollingOffset]);

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Resource Utilization Chart");
  }, [title]);

  const { filterState } = useGlobalFilters();
  const isFirstFilterRenderRef = useRef(true);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [viewResourceError, setViewResourceError] = useState("");
  const [viewDateError, setViewDateError] = useState("");
  const [shownPeriodLabel, setShownPeriodLabel] = useState(null);
  const viewResourcesRef = useRef(viewResources);
  const resourceSelectionDirtyRef = useRef(false);
  const chartRef = useRef(null);
  const chartWrapperRef = useRef(null);

  useEffect(() => {
    viewResourcesRef.current = viewResources;
  }, [viewResources]);

  useEffect(() => {
    const el = chartWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { chartRef.current?.resize(); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Derived display range — shown as period label before API responds
  const displayRange = useMemo(
    () => computeDisplayRange(selectedDate, granularity),
    [selectedDate, granularity],
  );

  // Update the granularity period label only when a new result arrives.
  // Intentionally omits viewMode/displayRange from deps — mode switches alone must not update the label.
  // In date range mode the label is set when Apply is clicked (see handleApplyDateRange).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!chartData || viewMode !== "granularity") return;
    setShownPeriodLabel(
      `${formatGermanDateLabel(displayRange.startDate)} – ${formatGermanDateLabel(displayRange.endDate)}`,
    );
  }, [chartData]);

  // --- Settings modal handlers ---

  const handleOpenSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);

  const handleApplySettings = useCallback(async (values) => {
    const normalizedGranularity = values.enabledGranularities.includes(granularity)
      ? granularity
      : values.enabledGranularities[0] || "day";

    const nextViewResources =
      values.configuredResources.length > 0
        ? (() => {
            const kept = viewResources.filter((r) =>
              values.configuredResources.includes(r),
            );
            return kept.length > 0 ? kept : values.configuredResources;
          })()
        : [];

    if (onTitleChange) onTitleChange(values.title);

    setTitle(values.title);
    setSelectedObjectType(values.selectedObjectType);
    setConfiguredResources(values.configuredResources);
    setTooltipAttributes(values.tooltipAttributes);
    setShiftStart(values.shiftStart);
    setShiftEnd(values.shiftEnd);
    setBreaks(values.breaks);
    setToleranceThreshold(values.toleranceThreshold);
    setIgnoreShortTransitions(values.ignoreShortTransitions);
    setIncludeNonWorkingDays(values.includeNonWorkingDays);
    setIncludeNoDataResources(values.includeNoDataResources);
    setMultiResourceMode(values.multiResourceMode);
    setEnabledGranularities(values.enabledGranularities);
    setComparisonEnabled(values.comparisonEnabled);
    setComparisonMode(values.comparisonMode);
    setComparisonPeriodsCount(values.comparisonPeriodsCount);
    setLimitComparisonToCurrentProgress(values.limitComparisonToCurrentProgress);
    setShowMovingAverage(values.showMovingAverage);
    setViewResources(nextViewResources);
    setGranularity(normalizedGranularity);
    setSelectedDate(values.selectedDate);
    setRollingOffset(values.rollingOffset);

    await fetchChartData({
      ...values,
      granularity: normalizedGranularity,
      viewResources: nextViewResources,
    });
  }, [
    fetchChartData,
    granularity,
    onTitleChange,
    viewResources,
    setBreaks,
    setConfiguredResources,
    setTooltipAttributes,
    setEnabledGranularities,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setSelectedObjectType,
    setShiftEnd,
    setShiftStart,
    setTitle,
    setToleranceThreshold,
    setGranularity,
    setViewResources,
    setSelectedDate,
    setRollingOffset,
    setComparisonEnabled,
    setComparisonMode,
    setComparisonPeriodsCount,
    setLimitComparisonToCurrentProgress,
    setShowMovingAverage,
  ]);

  // --- Re-fetch when global filters change ---

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    fetchMetadata();
    if (configuredResources.length > 0 && selectedObjectType && viewResources.length > 0) {
      fetchChartData();
    }
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Auto-load & refresh ---

  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(async () => {
        if (isMetadataLoaded) await fetchChartData();
      });
    }
  }, [onRefresh, isMetadataLoaded, fetchChartData]);

  // --- Header ---

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    ),
    [handleOpenSettings],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // --- View Mode handlers ---

  const triggerAutoApply = useCallback(
    (overrides = {}) => {
      const effectiveViewResources = overrides.viewResources ?? viewResourcesRef.current;
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart = overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (!effectiveViewResources || effectiveViewResources.length === 0) {
        setViewResourceError("Select at least one resource.");
        return false;
      }
      setViewResourceError("");

      if (effectiveViewMode === "daterange") {
        if (!effectiveDateRangeStart || !effectiveDateRangeEnd) {
          setViewDateError("Select start and end dates.");
          return false;
        }
        if (effectiveDateRangeStart > effectiveDateRangeEnd) {
          setViewDateError("Start date must be on or before end date.");
          return false;
        }
      }
      setViewDateError("");

      fetchChartData({
        viewResources: effectiveViewResources,
        viewMode: effectiveViewMode,
        dateRangeStart: effectiveDateRangeStart,
        dateRangeEnd: effectiveDateRangeEnd,
        ...overrides,
      });
      return true;
    },
    [fetchChartData, viewMode, dateRangeStart, dateRangeEnd],
  );

  const handleViewResourcesChange = useCallback(
    (nextResources) => {
      resourceSelectionDirtyRef.current = true;
      viewResourcesRef.current = nextResources;
      setViewResources(nextResources);
      setViewResourceError(
        nextResources.length > 0 ? "" : "Select at least one resource.",
      );
    },
    [setViewResources],
  );

  const handleResourceDropdownOpenChange = useCallback(
    (isOpen) => {
      if (isOpen || !resourceSelectionDirtyRef.current) return;
      resourceSelectionDirtyRef.current = false;
      if (viewMode !== "granularity") return;
      triggerAutoApply({ viewResources: viewResourcesRef.current });
    },
    [viewMode, triggerAutoApply],
  );

  const handleGranularityChange = useCallback(
    (g) => {
      setGranularity(g);
      const baseAnchor = selectedDate || defaultAnchor(g);
      const normalizedAnchor =
        datasetLastDate && baseAnchor > datasetLastDate
          ? datasetLastDate
          : baseAnchor;
      setSelectedDate(normalizedAnchor);
      triggerAutoApply({ granularity: g, selectedDate: normalizedAnchor });
    },
    [setGranularity, setSelectedDate, selectedDate, datasetLastDate, triggerAutoApply],
  );

  const handleViewModeChange = useCallback(
    (nextMode) => {
      if (nextMode === viewMode) return;
      if (nextMode !== "daterange") {
        const baseAnchor = dateRangeStart || selectedDate || defaultAnchor(granularity);
        const normalizedAnchor =
          datasetLastDate && baseAnchor > datasetLastDate
            ? datasetLastDate
            : baseAnchor;
        setSelectedDate(normalizedAnchor);
      }
      // Switching to daterange: leave dateRangeStart/End as their own independent state
      setViewMode(nextMode);
    },
    [
      viewMode,
      selectedDate,
      granularity,
      dateRangeStart,
      datasetLastDate,
      setSelectedDate,
      setViewMode,
    ],
  );

  const handleSelectedDateCommit = useCallback(
    (nextDate) => {
      setSelectedDate(nextDate);
      // A manual date edit detaches from any active rolling preset — manual
      // and rolling are mutually exclusive (mirrors ResourceStatusMonitor).
      setRollingOffset(null);
      if (viewMode === "granularity") {
        triggerAutoApply({ viewMode: "granularity", selectedDate: nextDate, rollingOffset: null });
      }
    },
    [setSelectedDate, setRollingOffset, viewMode, triggerAutoApply],
  );

  const handleDateRangeStartCommit = useCallback(
    (nextDate) => {
      setDateRangeStart(nextDate);
      setViewDateError("");
    },
    [setDateRangeStart],
  );

  const handleDateRangeEndCommit = useCallback(
    (nextDate) => {
      setDateRangeEnd(nextDate);
      setViewDateError("");
    },
    [setDateRangeEnd],
  );

  const handleApplyDateRange = useCallback(() => {
    setShownPeriodLabel(
      `${formatGermanDateLabel(dateRangeStart)} – ${formatGermanDateLabel(dateRangeEnd)}`,
    );
    triggerAutoApply({ viewMode: "daterange" });
  }, [triggerAutoApply, dateRangeStart, dateRangeEnd]);

  const handleDateRangePresetChange = useCallback(
    (value) => {
      const start = (value?.startDate || "").slice(0, 10);
      const end = (value?.endDate || "").slice(0, 10);
      setDateRangeStart(start);
      setDateRangeEnd(end);
      setViewDateError("");
      setShownPeriodLabel(`${formatGermanDateLabel(start)} – ${formatGermanDateLabel(end)}`);
      triggerAutoApply({ viewMode: "daterange", dateRangeStart: start, dateRangeEnd: end });
    },
    [setDateRangeStart, setDateRangeEnd, triggerAutoApply],
  );

  // --- Chart data ---

  // "Limit previous periods to current progress" also trims the CURRENT
  // period's own trailing buckets that haven't happened yet — not just the
  // comparison series — so the future doesn't render at all (not even as a
  // "no data" placeholder column). Since comparison series are zipped against
  // this array positionally further down, trimming it here also trims them.
  // Day granularity is excluded (no partial-period concept, mirrors
  // clampRangeEndToToday).
  const items = useMemo(() => {
    const rawItems = chartData?.chart_items || [];
    if (!limitComparisonToCurrentProgress) return rawItems;
    return trimFutureBuckets(rawItems, granularity, formatDateOnly(getBerlinToday()));
  }, [chartData, limitComparisonToCurrentProgress, granularity]);
  const hasNoDataBars = items.some((d) => d.no_data);

  const maxReal = useMemo(
    () =>
      Math.max(
        ...items.filter((d) => !d.no_data).map((d) => d.utilization_percentage),
        0,
      ),
    [items],
  );

  const CURRENT_COLOR = "#35DDA9";
  const GRAY_BG = "rgba(209, 213, 219, 0.7)";
  const GRAY_BORDER = "rgba(209, 213, 219, 1)";
  // Fixed 4-color recency ramp, index 0 = most recent past → oldest last. Combined with
  // the current period's CURRENT_COLOR (rendered last/rightmost), the full left→right
  // sequence for 4 comparison periods is: #495777, #2252BC, #3887D3, #41B6B8, #35DDA9.
  const COMPARISON_COLORS = ["#41B6B8", "#3887D3", "#2252BC", "#495777"];
  const getComparisonColor = (seriesIdx) => {
    const color = COMPARISON_COLORS[seriesIdx % COMPARISON_COLORS.length];
    return { bg: color, border: color };
  };

  const chartjsData = useMemo(() => {
    if (!items.length) return null;

    const fullHeight = Math.max(maxReal, 1);

    const labels = [], values = [], backgrounds = [], borders = [], noDataFlags = [];
    const hatchPattern = getHatchPattern();

    const labelGranularity = viewMode === "daterange" ? "day" : granularity;
    items.forEach((d) => {
      labels.push(formatXAxisDateLabel(d.bucket_label, labelGranularity));
      if (d.no_data) {
        values.push(fullHeight);
        backgrounds.push(hatchPattern || GRAY_BG);
        borders.push(GRAY_BORDER);
        noDataFlags.push(true);
      } else {
        const actualUtilization = Number(d.utilization_percentage || 0);
        const displayUtilization =
          actualUtilization > 0
            ? Math.max(actualUtilization, MIN_VISIBLE_UTILIZATION_PCT)
            : 0;
        values.push(displayUtilization);
        backgrounds.push(CURRENT_COLOR);
        borders.push(CURRENT_COLOR);
        noDataFlags.push(false);
      }
    });

    const mainDataset = {
      label: t('tiles:utilization.legend'),
      data: values,
      backgroundColor: backgrounds,
      borderColor: borders,
      borderWidth: 1,
      borderRadius: (ctx) => noDataFlags[ctx.dataIndex] ? 0 : 6,
      barPercentage: 0.7,
      categoryPercentage: 0.8,
      isCurrentPeriod: true,
    };

    // Comparison-period bars — aligned to the current period's bucket INDEX (not
    // calendar date), since periods can have different lengths (e.g. Feb vs Jan).
    // A comparison period with no bucket at a given index, or a no_data bucket,
    // simply leaves a gap (null) rather than a hatched bar.
    const comparisonSeries = chartData?.comparison_series || [];
    const comparisonDatasets = comparisonSeries.map((series, seriesIdx) => {
      const { bg, border } = getComparisonColor(seriesIdx);
      const compItems = series.chart_items || [];
      const compValues = items.map((_, i) => {
        const d = compItems[i];
        if (!d || d.no_data) return null;
        const actual = Number(d.utilization_percentage || 0);
        return actual > 0 ? Math.max(actual, MIN_VISIBLE_UTILIZATION_PCT) : 0;
      });
      return {
        label: series.period_label || `Previous period ${seriesIdx + 1}`,
        data: compValues,
        backgroundColor: bg,
        borderColor: border,
        borderWidth: 1,
        borderRadius: 6,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      };
    });

    // Within each bucket group, bars run oldest (left) → current period (right).
    // comparisonSeries is ordered most-recent-past → oldest, so reverse it before
    // appending the current period last.
    const datasets = [...comparisonDatasets.slice().reverse(), mainDataset];

    // Moving average — current period only, computed over real (non-no_data)
    // buckets so calendar gaps don't skew the window, then mapped back onto the
    // matching bucket index (null everywhere else, so Chart.js draws no point there).
    if (showMovingAverage) {
      const maWindow = MOVING_AVERAGE_WINDOW[granularity] || 7;
      // Real buckets immediately before period_start (backend, when
      // include_moving_average_lookback was requested) — seeds the rolling
      // window so the line can start at the first displayed bucket instead
      // of only once the window fills. Not displayed as bars themselves.
      const lookbackValues = (chartData?.lookback_chart_items || [])
        .filter((d) => !d.no_data)
        .map((d) => Number(d.utilization_percentage || 0));

      const realIndices = [];
      const realValues = [...lookbackValues];
      items.forEach((d, i) => {
        if (!d.no_data) {
          realIndices.push(i);
          realValues.push(Number(d.utilization_percentage || 0));
        }
      });
      const maRaw = computeMovingAverage(realValues, maWindow);
      const maForItems = maRaw.slice(lookbackValues.length);
      const maValues = new Array(items.length).fill(null);
      realIndices.forEach((idx, k) => { maValues[idx] = maForItems[k]; });
      datasets.push({
        type: "line",
        label: t('tiles:utilization.movingAverage', {
          window: t(`tiles:utilization.movingAverageWindow.${granularity}`),
        }),
        data: maValues,
        borderColor: "#374151",
        backgroundColor: "transparent",
        borderWidth: 2,
        pointRadius: 2,
        pointBackgroundColor: "#374151",
        tension: 0.3,
        spanGaps: true,
        order: 0,
      });
    }

    return { labels, datasets };
  }, [items, maxReal, granularity, viewMode, chartData, showMovingAverage]);

  // --- Click-triggered object breakdown tooltip ---
  // Replaces Chart.js's native hover tooltip: clicking a current-period bar
  // pins a portal panel with the same Utilization/Downtime/Available content
  // as before, plus (only when Task 2's tooltipAttributes is non-empty) a
  // scrollable object table below it, fetched via fetchObjectBreakdown.
  // { dataIndex, datasetIndex, left, top } — dataIndex is the bucket index,
  // datasetIndex distinguishes the current-period bar from a comparison-period
  // bar or moving-average point at the same bucket.
  const [selected, setSelected] = useState(null);
  const {
    data: objData,
    loading: objLoading,
    error: objError,
    request: requestObjectBreakdown,
  } = useObjectTableFetch();

  // Unit + type lookup for the object table's cell formatting, scoped to
  // this tile's own selectedObjectType (no tooltip-specific override, unlike
  // ResourceStatusMonitor's effectiveTooltipObjectType — this tile only ever
  // has one object type).
  const attributeUnitMap = useMemo(() => {
    const map = {};
    const schemas = selectedObjectType
      ? (metadata?.object_types_schema ?? []).filter((s) => s.name === selectedObjectType)
      : (metadata?.object_types_schema ?? []);
    for (const s of schemas) {
      for (const attr of (s.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit;
      }
    }
    return map;
  }, [metadata, selectedObjectType]);

  const attributeTypeMap = useMemo(() => {
    const map = {};
    const schemas = selectedObjectType
      ? (metadata?.object_types_schema ?? []).filter((s) => s.name === selectedObjectType)
      : (metadata?.object_types_schema ?? []);
    for (const s of schemas) {
      for (const attr of (s.attributes ?? [])) {
        if (attr.type) map[attr.name] = attr.type;
      }
    }
    return map;
  }, [metadata, selectedObjectType]);

  const formatCellValue = useCallback((attr, value) => {
    if (value == null) return "";
    const type = attributeTypeMap[attr];
    if (type === "timestamp" || type === "date") {
      return formatBerlinDisplay(value) ?? "";
    }
    const isNumericAttr = type === "float" || type === "integer" || type === "double" || type === "number";
    return formatAttributeValue(value, {
      hasDecimals: true,
      unit: attributeUnitMap[attr],
      isNumericAttr,
    });
  }, [attributeTypeMap, attributeUnitMap]);

  const buildRequestKey = useCallback(
    (item) => [activeFile, item.bucket_start, item.bucket_end, tooltipAttributes.join(" ")].join("|"),
    [activeFile, tooltipAttributes],
  );

  const chartjsOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    // Click now has one job: open the object-table panel. The summary
    // (Utilization/Downtime/Available) moved back to the native hover
    // tooltip below, so a click on a comparison-period/moving-average bar,
    // or on the current-period bar with no tooltipAttributes configured,
    // has nothing to show and is a no-op.
    onClick: (event, elements) => {
      if (!elements || elements.length === 0) return;
      if (!Array.isArray(tooltipAttributes) || tooltipAttributes.length === 0) return;
      const currentDatasetIdx = chartjsData?.datasets?.findIndex((d) => d.isCurrentPeriod) ?? -1;
      if (currentDatasetIdx < 0) return;
      const el = elements.find((e) => e.datasetIndex === currentDatasetIdx);
      if (!el) return;
      const dataIndex = el.index;
      const item = items[dataIndex];
      if (!item) return;

      const isToggleOff =
        selected && selected.dataIndex === dataIndex && selected.datasetIndex === currentDatasetIdx;
      const nextSelected = isToggleOff
        ? null
        : {
            dataIndex,
            datasetIndex: currentDatasetIdx,
            left: event.native?.clientX ?? 0,
            top: event.native?.clientY ?? 0,
          };
      setSelected(nextSelected);

      if (nextSelected) {
        requestObjectBreakdown(buildRequestKey(item), () =>
          fetchObjectBreakdown(item.bucket_start, item.bucket_end, tooltipAttributes),
        );
      }
    },
    plugins: {
      legend: {
        display: hasNoDataBars || comparisonEnabled || showMovingAverage,
        position: "top",
        labels: {
          generateLabels: () => {
            const comparisonSeries = chartData?.comparison_series || [];
            const comparisonEntries = comparisonSeries.map((series, seriesIdx) => {
              const { bg, border } = getComparisonColor(seriesIdx);
              return {
                text: series.period_label || `Previous period ${seriesIdx + 1}`,
                fillStyle: bg,
                strokeStyle: border,
                lineWidth: 1,
              };
            });

            // Mirrors the bar order: oldest comparison period first, current
            // period last (before moving average), left→right.
            const base = [
              ...comparisonEntries.slice().reverse(),
              { text: t('tiles:utilization.legend'), fillStyle: CURRENT_COLOR, strokeStyle: CURRENT_COLOR, lineWidth: 1 },
            ];
            if (hasNoDataBars) {
              base.push({ text: "No production data", fillStyle: getHatchPattern() || GRAY_BG, strokeStyle: GRAY_BORDER, lineWidth: 1 });
            }
            if (showMovingAverage) {
              base.push({
                text: t('tiles:utilization.movingAverage', {
                  window: t(`tiles:utilization.movingAverageWindow.${granularity}`),
                }),
                fillStyle: "transparent",
                strokeStyle: "transparent",
                lineWidth: 0,
                isMovingAverageIcon: true,
              });
            }
            return base;
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset?.isCurrentPeriod) {
              const d = items[context.dataIndex];
              if (!d) return "";
              if (d.no_data) return "No production data";
              const fmt = (n, dec = 1) => Number(n || 0).toFixed(dec);
              const actualUtilization = Number(d.utilization_percentage || 0);
              return [
                `Utilization: ${fmt(actualUtilization, 2)}%`,
                `Downtime: ${fmt(d.total_downtime_seconds / 3600, 2)} hrs`,
                `Available: ${fmt(d.available_time_seconds / 3600, 2)} hrs`,
              ];
            }
            const raw = context.parsed?.y;
            if (raw === null || raw === undefined) return null;
            const label = context.dataset?.label || "";
            return `${label}: ${Number(raw).toFixed(2)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t('common:chart.date'), color: "#4b5563", font: { size: 12 } },
        ticks: { maxRotation: 45, color: "#6b7280", font: { size: 11 } },
      },
      y: {
        title: { display: true, text: t('tiles:utilization.axis'), color: "#4b5563", font: { size: 12 } },
        ticks: { color: "#6b7280", font: { size: 11 } },
        beginAtZero: true,
      },
    },
  }), [hasNoDataBars, items, chartData, comparisonEnabled, showMovingAverage, granularity, chartjsData, tooltipAttributes, selected, buildRequestKey, requestObjectBreakdown, fetchObjectBreakdown]);

  // --- Derived state ---

  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showConfigPrompt =
    isMetadataLoaded && !loading && !error && configuredResources.length === 0;
  const showContent =
    isMetadataLoaded && !error && configuredResources.length > 0;
  const showChart = showContent && items.length > 0;
  const showNoData =
    showContent && !loading && chartData !== null && items.length === 0;
  const showSelectionPrompt = showContent && !loading && chartData === null;

  // Re-fetches whenever selectedDate advances past what was last fetched
  // (not just on the initial load) — otherwise a rolling date preset (e.g.
  // "Heute") rolling over to a new day only updates the displayed date
  // label, leaving chartData stale until a manual refresh. fetchChartData
  // itself de-dupes same-date/in-flight calls, so this can't cause redundant
  // network calls for the flows that already call it directly (manual date
  // pick, Settings apply, filter changes, refresh button).
  const lastFetchedDateRef = useRef(null);
  useEffect(() => {
    if (!showContent || loading || error) return;
    if (viewResources.length === 0) return;
    if (viewMode !== "granularity") return;
    if (chartData !== null && lastFetchedDateRef.current === selectedDate) return;
    lastFetchedDateRef.current = selectedDate;
    triggerAutoApply();
  }, [showContent, loading, error, chartData, viewResources, viewMode, selectedDate, triggerAutoApply]);

  return (
    <BaseTile title={title} loading={loading} minHeight="lg" className="h-full">
      <TileContent layout="flex" direction="column" gap="md" grow>
        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Load Resources Data"
            message="Load metadata first to configure available resources."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Load Resources Data
              </button>
            }
          />
        )}

        {showConfigPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Utilization Chart"
            message="Open settings to configure resources, shift, and granularities."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Configure Chart
              </button>
            }
          />
        )}

        {showContent && (
          <>
            {/* View Mode Controls */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 no-drag">
              {/* Row 1: Resources + Mode Toggle */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">{t('tiles:utilization.resources')}</label>
                  <div className="w-40">
                    <MultiSelectDropdown
                      options={configuredResources}
                      selectedValues={viewResources}
                      onSelectionChange={handleViewResourcesChange}
                      onOpenChange={handleResourceDropdownOpenChange}
                      placeholder="Select resources..."
                      itemType="resources"
                      className="text-sm w-full"
                      minOptionWidth="0"
                      explicitDisplayCount={5}
                    />
                  </div>
                </div>
                <div className="flex gap-0.5 bg-gray-200 rounded-md p-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("granularity")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "granularity"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {t('tiles:utilization.modeGranularity')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("daterange")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "daterange"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {t('tiles:utilization.modeDateRange')}
                  </button>
                </div>
              </div>
              {/* Row 2: Time Period controls */}
              <div className="flex items-center gap-1.5 mt-1.5">
                {viewMode === "granularity" ? (
                  <>
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('tiles:utilization.timePeriod')}</span>
                    <Tooltip.Provider delayDuration={200}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <button type="button" className="inline-flex text-gray-400 hover:text-gray-500 transition-colors" aria-label="Time period help">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content side="top" align="start" sideOffset={6} className="max-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg z-50">
                            <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
                              <span className="text-xs font-medium text-primary-700">Granularity Mode</span>
                            </div>
                            <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
                              Pick a granularity (<span className="text-primary-600 font-medium">Day, Week, Month, Year</span>) and select a date. Results are calculated for the full calendar period (Week = calendar week).
                            </div>
                            <Tooltip.Arrow className="fill-white" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                    {enabledGranularities.map((g) => {
                      const active = granularity === g;
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() => handleGranularityChange(g)}
                          className={`px-2 py-1 rounded-md border text-xs font-medium ${
                            active
                              ? "bg-primary-600 text-white border-primary-600"
                              : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          {t(`tiles:utilization.granularity.${g}`)}
                        </button>
                      );
                    })}
                    <DateInput
                      value={selectedDate}
                      onChange={handleSelectedDateCommit}
                      disabled={loading}
                    />
                  </>
                ) : (
                  <>
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('tiles:utilization.timePeriod')}</span>
                    <Tooltip.Provider delayDuration={200}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <button type="button" className="inline-flex text-gray-400 hover:text-gray-500 transition-colors" aria-label="Time period help">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content side="top" align="start" sideOffset={6} className="max-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg z-50">
                            <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
                              <span className="text-xs font-medium text-primary-700">Date Range Mode</span>
                            </div>
                            <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
                              Select an explicit <span className="text-primary-600 font-medium">start</span> and <span className="text-primary-600 font-medium">end date</span>. Click Apply to update the chart.
                            </div>
                            <Tooltip.Arrow className="fill-white" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                    <DateInput value={dateRangeStart} onChange={handleDateRangeStartCommit} disabled={loading} />
                    <span className="text-xs text-gray-500">to</span>
                    <DateInput value={dateRangeEnd} onChange={handleDateRangeEndCommit} disabled={loading} />
                    <button
                      type="button"
                      onClick={handleApplyDateRange}
                      disabled={loading || viewResources.length === 0}
                      className="px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-md hover:bg-primary-600 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? "Loading..." : "Apply"}
                    </button>
                  </>
                )}
              </div>
              {viewResourceError && <p className="mt-1 text-xs text-error-600">{viewResourceError}</p>}
              {viewMode === "daterange" && viewDateError && <p className="mt-1 text-xs text-error-600">{viewDateError}</p>}
              {viewMode === "daterange" && (
                <DateRangePresets
                  value={{ startDate: dateRangeStart || null, endDate: dateRangeEnd || null, enabled: true }}
                  onChange={handleDateRangePresetChange}
                />
              )}
            </div>

            {showChart && (
              <div className="flex-1 min-h-0 flex flex-col gap-3">
                <SummaryStats chartData={chartData} />
                {shownPeriodLabel && (
                  <div className="text-xs text-gray-800 text-center">
                    {shownPeriodLabel}
                  </div>
                )}
                <div ref={chartWrapperRef} className="flex-1 min-h-[180px] relative">
                  <div className="absolute inset-0">
                    <Chart type="bar" ref={chartRef} data={chartjsData} options={chartjsOptions} plugins={[movingAverageLegendIconPlugin]} />
                  </div>
                </div>

                {/* Click-triggered object-table panel — separate from the native
                    hover tooltip above. Anchored to the click point via a virtual
                    Popover anchor (Chart.js gives coordinates, not a DOM element),
                    pinned until dismissed (click elsewhere, Escape, or click the
                    same bar again). */}
                {selected !== null && (() => {
                  const dataset = chartjsData?.datasets?.[selected.datasetIndex];
                  if (!dataset || dataset.isCurrentPeriod !== true) return null;

                  return (
                    <PinnedInfoCard
                      open={selected !== null}
                      onOpenChange={(nextOpen) => { if (!nextOpen) setSelected(null); }}
                      anchorRect={{ left: selected.left, top: selected.top }}
                      side="top"
                      align="center"
                    >
                      <ObjectAttributeTable
                        attributes={tooltipAttributes}
                        rows={objData?.rows ?? []}
                        totalCount={objData?.total_count ?? 0}
                        loading={objLoading}
                        error={objError}
                        formatCellValue={formatCellValue}
                        leadingDivider={false}
                      />
                    </PinnedInfoCard>
                  );
                })()}
              </div>
            )}

            {showNoData && (
              <TileStatusContent
                status="empty"
                title="No Data Available"
                message="No utilization data found for the selected resources and configuration."
                icon={
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                }
              />
            )}

            {showSelectionPrompt && (
              <TileStatusContent
                status="empty"
                title="Select Filters to Load Chart"
                message={
                  viewMode === "granularity"
                    ? "Granularity mode applies automatically: granularity/date on commit and resources on dropdown close."
                    : "Date Range mode requires clicking Apply after selecting resources and dates."
                }
              />
            )}
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load chart"
            message={typeof error === "string" ? error : "Unexpected error."}
            action={
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isMetadataLoaded) fetchChartData();
                    else fetchMetadata();
                  }}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
                >
                  Retry
                </button>
                <button
                  onClick={handleOpenSettings}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Open Settings
                </button>
              </div>
            }
          />
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="Resource Utilization Chart Settings"
        getInitialValues={() => ({
          title,
          selectedObjectType,
          configuredResources: [...configuredResources],
          tooltipAttributes: [...tooltipAttributes],
          shiftStart,
          shiftEnd,
          breaks: breaks.map((b) => ({ ...b })),
          toleranceThreshold,
          ignoreShortTransitions,
          includeNonWorkingDays,
          includeNoDataResources,
          multiResourceMode,
          enabledGranularities: [...enabledGranularities],
          comparisonEnabled,
          comparisonMode,
          comparisonPeriodsCount,
          limitComparisonToCurrentProgress,
          showMovingAverage,
          selectedDate,
          rollingOffset,
        })}
        onApply={handleApplySettings}
        isApplyDisabled={(values) =>
          !(
            isMetadataLoaded &&
            values.selectedObjectType &&
            values.configuredResources?.length > 0 &&
            selectedDate &&
            values.enabledGranularities?.length > 0
          ) || loading
        }
        applyButtonText={loading ? "Calculating..." : "Apply and Calculate"}
      >
        {({ values, setField, update }) => (
          <ResourceUtilizationChartSettings
            title={values.title}
            objectTypes={objectTypes}
            selectedObjectType={values.selectedObjectType}
            availableResources={availableResources}
            configuredResources={values.configuredResources}
            activeFile={activeFile}
            tooltipAttributes={values.tooltipAttributes}
            shiftStart={values.shiftStart}
            shiftEnd={values.shiftEnd}
            breaks={values.breaks}
            toleranceThreshold={values.toleranceThreshold}
            ignoreShortTransitions={values.ignoreShortTransitions}
            includeNonWorkingDays={values.includeNonWorkingDays}
            includeNoDataResources={values.includeNoDataResources}
            multiResourceMode={values.multiResourceMode}
            enabledGranularities={values.enabledGranularities}
            comparisonEnabled={values.comparisonEnabled}
            comparisonMode={values.comparisonMode}
            comparisonPeriodsCount={values.comparisonPeriodsCount}
            limitComparisonToCurrentProgress={values.limitComparisonToCurrentProgress}
            showMovingAverage={values.showMovingAverage}
            comparisonDisabledReason={
              viewMode === "daterange"
                ? "Comparison periods are only available in Granularity mode."
                : ""
            }
            selectedDate={values.selectedDate}
            rollingOffset={values.rollingOffset}
            resourceLastActiveDate={resourceLastActiveDate}
            viewMode={viewMode}
            onSelectedDateChange={(dateStr, offset) =>
              update((p) => ({ ...p, selectedDate: dateStr, rollingOffset: offset }))
            }
            onTitleChange={(v) => setField("title", v)}
            onObjectTypeChange={(v) => setField("selectedObjectType", v)}
            onTooltipAttributesChange={(v) => setField("tooltipAttributes", v)}
            onConfiguredResourcesChange={(v) =>
              setField("configuredResources", v)
            }
            onShiftStartChange={(v) => setField("shiftStart", v)}
            onShiftEndChange={(v) => setField("shiftEnd", v)}
            onAddBreak={() =>
              update((p) => ({
                ...p,
                breaks: [...p.breaks, { start: "12:00", end: "12:30" }],
              }))
            }
            onRemoveBreak={(idx) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.filter((_, i) => i !== idx),
              }))
            }
            onUpdateBreak={(idx, field, val) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.map((b, i) =>
                  i === idx ? { ...b, [field]: val } : b,
                ),
              }))
            }
            onToleranceThresholdChange={(v) =>
              setField("toleranceThreshold", v)
            }
            onIgnoreShortTransitionsChange={(v) =>
              setField("ignoreShortTransitions", v)
            }
            onIncludeNonWorkingDaysChange={(v) =>
              setField("includeNonWorkingDays", v)
            }
            onIncludeNoDataResourcesChange={(v) =>
              setField("includeNoDataResources", v)
            }
            onMultiResourceModeChange={(v) => setField("multiResourceMode", v)}
            onEnabledGranularitiesChange={(v) =>
              setField("enabledGranularities", v)
            }
            onComparisonEnabledChange={(v) => setField("comparisonEnabled", v)}
            onComparisonModeChange={(v) => setField("comparisonMode", v)}
            onComparisonPeriodsCountChange={(v) =>
              setField("comparisonPeriodsCount", v)
            }
            onLimitComparisonToCurrentProgressChange={(v) =>
              setField("limitComparisonToCurrentProgress", v)
            }
            onShowMovingAverageChange={(v) => setField("showMovingAverage", v)}
            loading={loading}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
}

export default ResourceUtilizationChart;



